import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { apiService } from '../services/api';
import { CartOut, CartItemCreate, OrderOut } from '../types/api';
import { useOrderHistory } from './OrderHistoryContext';
import { useAuth } from './AuthContext';
import { handleStockError, StockError } from '../utils/errorHandling';

interface CartContextType {
  cart: CartOut | null;
  isLoading: boolean;
  currentEventId: number | null;
  currentVenueId: number | null;
  setCurrentEventId: (eventId: number) => void;
  setCurrentVenueId: (venueId: number) => void;
  addToCart: (item: CartItemCreate) => Promise<StockError | null>;
  updateCartItem: (itemId: number, quantity: number) => Promise<StockError | null>;
  removeFromCart: (itemId: number) => Promise<void>;
  clearCart: () => Promise<void>;
  checkout: (orderStatus?: string, paymentMethod?: 'cash' | 'card') => Promise<OrderOut>;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cart, setCart] = useState<CartOut | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentEventId, setCurrentEventId] = useState<number | null>(null);
  const [currentVenueId, setCurrentVenueId] = useState<number | null>(null);
  const { addOrder } = useOrderHistory();
  const { isAuthenticated } = useAuth();
  const prevEventIdRef = useRef<number | null>(null);

  const refreshCart = async () => {
    try {
      setIsLoading(true);
      
      if (isAuthenticated) {
        // Use authenticated cart endpoints
        if (!currentEventId) {
          console.log('No event ID set for authenticated cart operations');
          return;
        }
        console.log('🔄 Refreshing authenticated cart for event:', currentEventId);
        const cartData = await apiService.getCart(currentEventId);
        console.log('📦 Authenticated cart data received:', cartData);
        
        if (cartData && cartData.items) {
          setCart(cartData);
          console.log('✅ Authenticated cart state refreshed successfully');
        } else {
          console.warn('⚠️ Invalid authenticated cart data received:', cartData);
          setCart(null);
        }
      } else {
        // Use anonymous cart endpoints
        if (!currentEventId) {
          console.log('No event ID set for anonymous cart operations');
          return;
        }
        console.log('🔄 Refreshing anonymous cart for event:', currentEventId);
        const cartData = await apiService.getAnonymousCart(currentEventId);
        console.log('📦 Anonymous cart data received:', cartData);
        
        if (cartData && cartData.items) {
          setCart(cartData);
          console.log('✅ Anonymous cart state refreshed successfully');
        } else {
          console.warn('⚠️ Invalid anonymous cart data received:', cartData);
          setCart(null);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching cart:', error);
      setCart(null);
    } finally {
      setIsLoading(false);
    }
  };

  const addToCart = async (item: CartItemCreate): Promise<StockError | null> => {
    try {
      setIsLoading(true);
      
      if (isAuthenticated) {
        // Use authenticated cart endpoints
        if (!currentEventId) {
          console.log('No event ID set for authenticated cart operations');
          return null;
        }
        console.log('Adding to authenticated cart:', item, 'for event:', currentEventId);
        const updatedCart = await apiService.addToCart(item, currentEventId);
        console.log('Authenticated cart updated:', updatedCart);
        
        if (updatedCart && updatedCart.items) {
          setCart(updatedCart);
          console.log('Authenticated cart state updated successfully');
        } else {
          console.warn('Invalid authenticated cart response:', updatedCart);
        }
      } else {
        // Use anonymous cart endpoints
        if (!currentEventId) {
          console.log('No event ID set for anonymous cart operations');
          return null;
        }
        console.log('Adding to anonymous cart:', item, 'for event:', currentEventId);
        const updatedCart = await apiService.addToAnonymousCart(item, currentEventId);
        console.log('Anonymous cart updated:', updatedCart);
        
        if (updatedCart && updatedCart.items) {
          setCart(updatedCart);
          console.log('Anonymous cart state updated successfully');
        } else {
          console.warn('Invalid anonymous cart response:', updatedCart);
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error adding to cart:', error);
      const stockError = handleStockError(error);
      if (stockError) {
        return stockError;
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateCartItem = async (itemId: number, quantity: number): Promise<StockError | null> => {
    try {
      setIsLoading(true);
      
      if (isAuthenticated) {
        // Use authenticated cart endpoints
        if (!currentEventId) {
          throw new Error('No event ID set for authenticated cart operations');
        }
        const updatedCart = await apiService.updateCartItem(itemId, quantity, currentEventId);
        setCart(updatedCart);
      } else {
        // Use anonymous cart endpoints
        if (!currentEventId) {
          throw new Error('No event ID set for anonymous cart operations');
        }
        const updatedCart = await apiService.updateAnonymousCartItem(currentEventId, itemId, quantity);
        setCart(updatedCart);
      }
      
      return null;
    } catch (error) {
      console.error('Error updating cart item:', error);
      const stockError = handleStockError(error);
      if (stockError) {
        return stockError;
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (itemId: number) => {
    try {
      setIsLoading(true);
      
      if (isAuthenticated) {
        // Use authenticated cart endpoints
        if (!currentEventId) {
          console.log('No event ID set for authenticated cart operations');
          return;
        }
        console.log('Removing from authenticated cart:', itemId, 'for event:', currentEventId);
        const updatedCart = await apiService.removeFromCart(itemId, currentEventId);
        console.log('Authenticated cart updated after removal:', updatedCart);
        setCart(updatedCart);
      } else {
        // Use anonymous cart endpoints
        if (!currentEventId) {
          console.log('No event ID set for anonymous cart operations');
          return;
        }
        console.log('Removing from anonymous cart:', itemId, 'for event:', currentEventId);
        const updatedCart = await apiService.removeFromAnonymousCart(itemId, currentEventId);
        console.log('Anonymous cart updated after removal:', updatedCart);
        setCart(updatedCart);
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setIsLoading(true);
      
      if (isAuthenticated) {
        // Use authenticated cart endpoints
        if (!currentEventId) {
          console.log('No event ID set for authenticated cart operations');
          return;
        }
        console.log('Clearing authenticated cart for event:', currentEventId);
        await apiService.clearCart(currentEventId);
        console.log('Authenticated cart cleared successfully');
        setCart(null);
      } else {
        // Use anonymous cart endpoints
        if (!currentEventId) {
          console.log('No event ID set for anonymous cart operations');
          return;
        }
        console.log('Clearing anonymous cart for event:', currentEventId);
        await apiService.clearAnonymousCart(currentEventId);
        console.log('Anonymous cart cleared successfully');
        setCart(null);
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const checkout = async (orderStatus?: string, paymentMethod?: 'cash' | 'card'): Promise<OrderOut> => {
    try {
      setIsLoading(true);
      
      if (!cart || !cart.items || cart.items.length === 0) {
        console.log('No items in cart for checkout');
        throw new Error('No items in cart for checkout');
      }
      
      if (isAuthenticated) {
        // Use authenticated cart endpoints
        if (!currentEventId) {
          console.log('No event ID set for authenticated cart operations');
          throw new Error('No event ID set for checkout');
        }
        console.log('Checking out authenticated cart for event:', currentEventId);
        console.log('Cart items:', cart.items);
        const order = await apiService.checkoutCart(currentEventId, orderStatus, paymentMethod);
        console.log('Authenticated checkout successful:', order);
        
        // Save order to session history
        addOrder(order);
        console.log('Order saved to history:', order.id);
        
        setCart(null); // Clear cart after successful checkout
        return order;
      } else {
        // Use anonymous cart endpoints
        if (!currentEventId) {
          console.log('No event ID set for anonymous cart operations');
          throw new Error('No event ID set for checkout');
        }
        console.log('Checking out anonymous cart for event:', currentEventId);
        console.log('Cart items:', cart.items);
        const order = await apiService.checkoutAnonymousCart(currentEventId, cart, orderStatus, paymentMethod);
        console.log('Anonymous checkout successful:', order);
        
        // Save order to session history
        addOrder(order);
        console.log('Order saved to history:', order.id);
        
        setCart(null); // Clear cart after successful checkout
        return order;
      }
    } catch (error) {
      console.error('Error checking out cart:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Load cart on mount and when currentEventId or authentication status changes
  useEffect(() => {
    console.log('CartContext: currentEventId changed to:', currentEventId, 'previous:', prevEventIdRef.current, 'isAuthenticated:', isAuthenticated);
    
    if (isAuthenticated) {
      // For authenticated users, refresh cart when event ID changes
      if (currentEventId && currentEventId !== prevEventIdRef.current) {
        console.log('CartContext: User authenticated, event ID changed, refreshing cart for event:', currentEventId);
        prevEventIdRef.current = currentEventId;
        refreshCart();
      } else if (currentEventId === prevEventIdRef.current) {
        console.log('CartContext: Event ID unchanged, skipping refresh');
      }
    } else if (currentEventId && currentEventId !== prevEventIdRef.current) {
      // For anonymous users, only refresh if event ID changed
      console.log('CartContext: Event ID changed, refreshing anonymous cart for event:', currentEventId);
      prevEventIdRef.current = currentEventId;
      refreshCart();
    } else if (currentEventId === prevEventIdRef.current) {
      console.log('CartContext: Event ID unchanged, skipping refresh');
    }
  }, [currentEventId, isAuthenticated]); // refreshCart is stable, no need to add it as dependency

  const value: CartContextType = {
    cart,
    isLoading,
    currentEventId,
    currentVenueId,
    setCurrentEventId,
    setCurrentVenueId,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    checkout,
    refreshCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
